import { $createBoundTask } from "@captions-web/dev-logger";
import { Icon } from "@captions-web/icons";
import { createAndDownloadZip } from "@captions-web/utils/zip-file";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { t } from "i18next";
import { Fragment, useState } from "react";
import { toast } from "sonner";

import { LayoutBleed } from "~/components/layouts/bleed";
import { Button, LinkButton } from "~/components/primitives/button";
import { DropdownMenu } from "~/components/primitives/dropdown-menu";
import { Spinner } from "~/components/primitives/spinner";
import { textTruncateStyles } from "~/components/primitives/text-truncate";
import { ProjectVideoEvents } from "~/lib/analytics/defs/ProjectVideoEvents";
import {
  forceDownload,
  getFileNameForClipVariantDownload,
  getFileNameForVideoDownload,
  getFileNameForVideoZipDownload,
  getFileNameForAudioDownload,
  getFolderNameForClipFolderDownload,
} from "~/lib/download";
import { getErrorTypeFromNetworkError } from "~/lib/network/known-api-error";
import { AudioProcessor } from "~/modules/audio/audio-processor";
import { CreditsPaywallDialog } from "~/modules/credits/components/credits-paywall";
import { ClipSequenceSelector } from "~/modules/project/components/clip-sequence-selector/clip-sequence-selector";
import { useDeleteVideo } from "~/modules/project/hooks/use-delete-video";
import {
  exportVideoForClipsMutation,
  regenerateVideoClipMutation,
  selectVideoClipForIndexMutation,
  exportVideoForVideoIdMutation,
} from "~/modules/project/network/mutations";
import { getProjectDetailsQuery, getVideoDetailsQuery } from "~/modules/project/network/queries";
import { useClipSequenceSelectorStore } from "~/modules/project/stores/clip-sequence-selector";
import { css } from "~/styled-system/css";

const VIDEO_DETAILS_STALE_TIME = 60 * 1000;

export const Route = createFileRoute("/_app/projects/$projectId/videos/$videoId")({
  component: RouteComponent,
  async loader({ params: { videoId, projectId }, context: { queryClient } }) {
    // prefetch the project details, we do not need to block on this
    queryClient.prefetchQuery(
      getProjectDetailsQuery.queryOptions({ params: { id: projectId } }, { staleTime: Infinity })
    );

    // and then block on video details to make sure that the video is completed
    const video = await queryClient.ensureQueryData(
      getVideoDetailsQuery.queryOptions({ params: { videoId, projectId } })
    );

    if (!video) {
      throw redirect({
        to: "/projects/$projectId",
        params: { projectId },
      });
    }

    return { video };
  },
  pendingComponent: RoutePending,
});

// having "empty" pending component keeps the shell rendering and just blank content
// we can add shell or other loading state in the future if we want
function RoutePending() {
  return null;
}

function RouteComponent() {
  const { projectId, videoId } = Route.useParams();
  const { data: video } = getVideoDetailsQuery.useQuery(
    { params: { projectId, videoId } },
    { staleTime: VIDEO_DETAILS_STALE_TIME }
  );
  const [currentCompositedFile] = useClipSequenceSelectorStore((s) => [s.currentCompositedFile]);
  const queryClient = useQueryClient();
  const [showCreditsPaywall, setShowCreditsPaywall] = useState(false);

  const { mutateAsync: triggerRegeneration } = regenerateVideoClipMutation.useMutation({
    onError: (error, { params: { clipIndex } }) => {
      console.error(error);

      if (getErrorTypeFromNetworkError(error) === "INSUFFICIENT_CREDITS") {
        setShowCreditsPaywall(true);
      } else if (getErrorTypeFromNetworkError(error) === "NUM_VIDEOS_QUEUE_EXCEEDED") {
        toast.error(t("mirage-studio:video-actions.errors.regenerate-clip-queue-exceeded"));
      } else {
        toast.error(
          t("mirage-studio:video-actions.errors.regenerate-clip", { message: error.message })
        );
      }

      ProjectVideoEvents.clip_regenerate_submit_status.track({
        status: "error",
        video_id: videoId,
        project_id: projectId,
        clip_index: clipIndex,
        error_message: error.message,
      });
    },
    onSettled: async () => {
      await getVideoDetailsQuery.invalidateQuery(queryClient, { params: { projectId, videoId } });
    },
    onSuccess: (_, { params: { clipIndex } }) => {
      ProjectVideoEvents.clip_regenerate_submit_status.track({
        status: "success",
        video_id: videoId,
        project_id: projectId,
        clip_index: clipIndex,
      });
    },
  });

  const { mutate: selectVideoClip } = selectVideoClipForIndexMutation.useMutation({
    onMutate: async (newClipSelection) => {
      getVideoDetailsQuery.setQueryData(
        queryClient,
        { params: { projectId, videoId } },
        (previousData) => {
          if (!previousData) {
            return previousData;
          }
          const nextClips = previousData.clips.map((clip, index) =>
            index === newClipSelection.params.clipIndex
              ? { ...clip, selectedClipId: newClipSelection.params.clipId }
              : clip
          );
          return { ...previousData, clips: nextClips };
        }
      );
    },
    onSuccess: (_, { params: { clipIndex } }) => {
      ProjectVideoEvents.clip_select_status.track({
        status: "success",
        video_id: videoId,
        project_id: projectId,
        clip_index: clipIndex,
      });
    },
    onError: (error, { params: { clipIndex } }) => {
      console.error(error);
      toast.error(t("mirage-studio:video-actions.errors.save-clip-selection"));

      ProjectVideoEvents.clip_select_status.track({
        status: "error",
        video_id: videoId,
        project_id: projectId,
        clip_index: clipIndex,
        error_message: error.message,
      });
    },
  });

  const { data: projectMetadata } = getProjectDetailsQuery.useQuery(
    { params: { id: projectId } },
    { staleTime: Infinity }
  );

  const { mutate: exportVideoForClips, isPending: isExporting } = useMutation({
    mutationFn: async () => {
      // setup scoped task tracking
      const $task = $createBoundTask({
        getMetadata: () => ({
          video_id: videoId,
          project_id: projectId,
          clip_count: video?.clips?.length ?? 0,
        }),
        trackEvent: ProjectVideoEvents.download_status.track,
      });
      // default to backend export using export video for clips endpoint
      try {
        const res = await $task("backend-export", () => {
          const clipIds = video?.clips.map((clipIndexItem) => clipIndexItem.selectedClipId);
          if (!clipIds) {
            throw new Error("clipIds is undefined");
          }
          return exportVideoForClipsMutation.trigger({ params: { clipIds, videoId } });
        });
        await $task("trigger-download-backend-export", () => {
          const { url: downloadUrl } = res;
          const filename = getFileNameForVideoDownload(downloadUrl, videoId);
          return forceDownload(downloadUrl, filename);
        });
        // if succeed, exit early to prevent execution of fallback
        return;
      } catch (error) {
        console.error(error);
      }
      // fallback logic using client side composited file for download
      await $task("fallback-client-side-export", () => {
        const url = currentCompositedFile?.url;
        if (!url) {
          throw new Error("fallback client side download failed due to undefined blob url");
        }
        const filename = getFileNameForVideoDownload(url, videoId);
        return forceDownload(url, filename);
      });
    },
    onError: (error) => {
      console.error(error);
      toast.error(t("mirage-studio:video-actions.errors.download-failed"));
    },
  });

  const { mutate: triggerRegenerateAll, isPending: isRegeneratingAll } = useMutation({
    mutationFn: async () => {
      if (!video) {
        return;
      }
      // generate in sequence to prevent dirty reads causing some newly regenerated clips to get orphaned
      // running these requests in parallel doesn't provide consistency guarantees
      for (let i = 0; i < video.clips.length; i++) {
        await triggerRegeneration({ params: { clipIndex: i, videoId, projectId } });
      }

      ProjectVideoEvents.clip_regenerate_all_submit_status.track({
        status: "success",
        video_id: videoId,
        project_id: projectId,
        clip_count: video.clips.length,
      });
      await getVideoDetailsQuery.invalidateQuery(queryClient, { params: { projectId, videoId } });
    },
    onError: (error) => {
      console.error(error);
      toast.error(t("mirage-studio:video-actions.errors.regenerate-all-clips"));

      ProjectVideoEvents.clip_regenerate_all_submit_status.track({
        status: "error",
        video_id: videoId,
        project_id: projectId,
        clip_count: video?.clips.length ?? -1,
        error_message: error.message,
      });
    },
  });

  const {
    data: { project },
  } = getProjectDetailsQuery.useSuspenseQuery({
    params: {
      id: projectId,
    },
  });

  const handleRegenerate = async (clipIndex: number) => {
    await triggerRegeneration({ params: { clipIndex, videoId, projectId } });
  };

  const handleSelectClip = (clipIndex: number, clipId: string) => {
    selectVideoClip({
      params: {
        projectId,
        clipId,
        clipIndex,
        videoId,
      },
    });
  };

  const handleRegenerateAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    triggerRegenerateAll();
  };

  const { mutate: triggerDownloadToZip, isPending: isDownloadingToZip } = useMutation({
    mutationFn: async () => {
      // setup scoped task tracking
      const $task = $createBoundTask({
        getMetadata: () => ({
          video_id: videoId,
          project_id: projectId,
          clip_count: video?.clips?.length ?? 0,
        }),
        trackEvent: ProjectVideoEvents.export_all_as_zip.track,
      });
      const videoFile = await $task("video-export", async () => {
        try {
          const res = await exportVideoForVideoIdMutation.trigger({ params: { videoId } });
          return { url: res.url, filename: getFileNameForVideoDownload(res.url, videoId) };
        } catch (error) {
          // swallow error to allow client side export
          console.error(error);
          const url = currentCompositedFile?.url as string;
          return {
            url,
            filename: getFileNameForVideoDownload(url, videoId),
          };
        }
      });

      const audioFile = await $task("audio-export", async () => {
        const audioId = video?.audioId;
        const audio = projectMetadata?.project.audios.find((item) => item.id === audioId);
        let audioUrl = audio?.url;
        if (!audioUrl) {
          if (!currentCompositedFile || !currentCompositedFile.url || !currentCompositedFile.file) {
            throw new Error("unable to do full zip export: composited file not ready");
          }
          if (!currentCompositedFile.file) {
            throw new Error(
              "compostited file has no URL, video has invalid audio id, unable to export all as zip"
            );
          }
          const audioProcessor = new AudioProcessor();
          await audioProcessor.loadAudio(currentCompositedFile.file as File);
          const file = audioProcessor.trimAudioFile({
            startMs: 0,
            endMs: audioProcessor.durationMs,
          });
          if (file) {
            audioUrl = URL.createObjectURL(file);
          }
        }
        if (!audioUrl) {
          throw new Error("unable to resolve audio url");
        }
        return { url: audioUrl, filename: getFileNameForAudioDownload(videoId) };
      });

      const clipFolders = await $task("download-clips", async () => {
        if (!video?.clips) {
          return [];
        }
        return video.clips.map((clip, clipIndex) => {
          const completeVariants = clip.variants.filter((variant) => variant.state === "COMPLETE");
          return {
            folderName: getFolderNameForClipFolderDownload(videoId, clipIndex),
            files: completeVariants.map((variant, variantIndex) => ({
              url: variant.videoUrl,
              filename: getFileNameForClipVariantDownload(videoId, clipIndex, variantIndex),
            })),
          };
        });
      });
      await $task("create-zip", async () => {
        const zipFilename = getFileNameForVideoZipDownload(videoId);
        const files = [videoFile, audioFile];
        const folders = [...clipFolders];
        await createAndDownloadZip({ files, folders }, zipFilename);
        return;
      });
    },
    onError: (error) => {
      console.error(error);
      toast.error(t("mirage-studio:video-actions.errors.download-failed"));
    },
  });

  const handleDownloadAsZip = (e: React.MouseEvent) => {
    e?.stopPropagation();
    e.preventDefault();
    triggerDownloadToZip();
  };

  const { deleteVideo, isDeleting } = useDeleteVideo({
    location: "video-detail-page",
    navigateToOnSuccess: {
      to: "/projects/$projectId",
      params: { projectId },
    },
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    deleteVideo({ projectId, videoId });
  };

  return (
    <LayoutBleed
      title={
        <span
          className={css(
            {
              maxWidth: "400px",
              display: "inline-block",
            },
            textTruncateStyles.raw({ truncated: "1-line" })
          )}
        >
          {project.title || t("mirage-studio:project-detail.title-untitled")}
        </span>
      }
      leftActions={
        <LinkButton
          to="/projects/$projectId"
          variant="secondary"
          size="md"
          iconPosition="only"
          icon={<Icon name="left-arrow-small" />}
        />
      }
      rightActions={
        <Fragment>
          <Button
            variant="primary"
            size="md"
            icon={<Icon name="download" />}
            iconPosition="before"
            onClick={() => exportVideoForClips()}
            disabled={isExporting}
            isPending={isExporting}
          >
            {t("mirage-studio:video-actions.download.video")}
          </Button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="secondary"
                size="md"
                iconPosition="only"
                icon={<Icon name="options" />}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content side="bottom" sideOffset={8} align="end">
              <DropdownMenu.Item
                icon={<Icon name="folder-download" />}
                onClick={handleDownloadAsZip}
                disabled={isDownloadingToZip}
              >
                {t("mirage-studio:video-actions.download.as-zip")}
                {isDownloadingToZip && <Spinner size={16} />}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                icon={<Icon name="flip" />}
                onClick={handleRegenerateAll}
                disabled={isRegeneratingAll}
              >
                {t("mirage-studio:video-actions.regenerate.all-clips")}
                {isRegeneratingAll && <Spinner size={16} />}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                destructive
                icon={<Icon name="delete-bare" />}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {t("mirage-studio:video-actions.delete.video")}
                {isDeleting && <Spinner size={16} />}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Fragment>
      }
    >
      <CreditsPaywallDialog onOpenChange={setShowCreditsPaywall} open={showCreditsPaywall} />

      <div className={rootStyles}>
        <ClipSequenceSelector
          clips={video?.clips ?? []}
          onRegenerate={handleRegenerate}
          onSelect={handleSelectClip}
          videoId={videoId}
          projectId={projectId}
        />
      </div>
    </LayoutBleed>
  );
}

const rootStyles = css({
  flex: 1,
  height: "100%",
  maxHeight:
    "calc(100dvh - ((token(spacing.bleedLayoutPadding) * 2) + token(sizes.bleedHeaderHeight)))",
  padding: "16px",
});
